import { useStore } from '@hooks/useStore'
import { CheckCircle, Error as ErrorIcon, Microsoft } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { ERRORS } from '@constants'
import { IMicrosoftOAuthStatus } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

export const MicrosoftOAuthConnect = observer(() => {
  const { api, company } = useStore()
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<IMicrosoftOAuthStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = async () => {
    setLoading(true)
    setError(null)
    try {
      const microsoftStatus = await api.oauth.getMicrosoftStatus()
      setStatus(microsoftStatus)
    } catch (err: any) {
      console.error('Error fetching Microsoft status:', err)
      setStatus({ connected: false })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()

    // Check URL params for OAuth callback success/error
    const params = new URLSearchParams(window.location.search)
    if (params.get('microsoft_connected') === 'true') {
      fetchStatus()
      // Remove the param from URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (params.get('microsoft_error') === 'personal_account') {
      setError(ERRORS.MICROSOFT_OAUTH.PERSONAL_ACCOUNT)
      // Remove the param from URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (params.get('microsoft_error') === 'true') {
      setError(ERRORS.MICROSOFT_OAUTH.FAILED)
      // Remove the param from URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleConnect = async () => {
    setLoading(true)
    setError(null)
    try {
      // First, save the email provider as 'microsoft' before OAuth redirect
      // This ensures the backend knows which provider to test after OAuth completes
      if (company.current_company) {
        await company.update({
          ...company.current_company,
          email_provider: 'microsoft'
        })
      }

      // Get Microsoft OAuth URL from backend
      const authUrl = await api.oauth.getMicrosoftAuthUrl()
      // Redirect to Microsoft OAuth flow (opens in current window)
      window.location.href = authUrl
      // Loading will be cleared when user returns from OAuth
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Verbinding mislukt')
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Weet je zeker dat je Microsoft wilt ontkoppelen?')) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      await api.oauth.disconnectMicrosoft()
      setStatus({ connected: false })
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Ontkoppelen mislukt')
    } finally {
      setLoading(false)
    }
  }

  if (loading && !status) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                Microsoft / Outlook Verbinding
              </Typography>
              {status?.connected && status.email && (
                <Typography variant="body2" color="text.secondary">
                  {status.email}
                  {status.name && ` (${status.name})`}
                </Typography>
              )}
            </Box>
            <Chip
              label={status?.connected ? 'Verbonden' : 'Niet verbonden'}
              color={status?.connected ? 'success' : 'default'}
              size="small"
              icon={status?.connected ? <CheckCircle /> : <Microsoft />}
            />
          </Stack>

          {error && (
            <Alert severity="error" icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {status?.connected ? (
            <Stack spacing={1}>
              {status.is_expired && (
                <Alert severity="warning">
                  Token is verlopen. Koppel opnieuw om e-mails te versturen.
                </Alert>
              )}
              {status.connected_at && (
                <Typography variant="caption" color="text.secondary">
                  Verbonden op: {new Date(status.connected_at).toLocaleDateString('nl-NL')}
                </Typography>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={loading}
                fullWidth>
                {loading ? 'Ontkoppelen...' : 'Ontkoppel Microsoft'}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Microsoft />}
              fullWidth>
              {loading ? 'Verbinden...' : 'Verbind met Microsoft'}
            </Button>
          )}

          {!status?.connected && (
            <Typography variant="caption" color="text.secondary">
              Door te verbinden met Microsoft kun je facturen en offertes versturen vanaf je eigen
              Outlook/Microsoft 365-account.
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
})
